// Custom hook to load all products from the backend API

import { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const CB = `_t=${Date.now()}`;

const useProducts = () => {
  const [brazosGitanos, setBrazosGitanos] = useState([]);
  const [galletas, setGalletas] = useState([]);
  const [galletasEspeciales, setGalletasEspeciales] = useState([]);
  const [packs, setPacks] = useState([]);
  const [pasteles, setPasteles] = useState([]);
  const [panaderia, setPanaderia] = useState([]);
  const [desayunos, setDesayunos] = useState([]);
  const [menuSalado, setMenuSalado] = useState([]);
  const [bebidasFrias, setBebidasFrias] = useState([]);
  const [bebidasCalientes, setBebidasCalientes] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      
      // Brazos
      const brazosResponse = await fetch(`${BACKEND_URL}/api/products?category=brazos&${CB}`);
      if (brazosResponse.ok) {
        const brazosData = await brazosResponse.json();
        setBrazosGitanos(brazosData.map(b => ({
          id: b.id,
          name: b.name,
          slug: b.slug,
          description: b.description,
          priceSlice: b.priceSlice,
          priceFull: b.priceFull,
          image: b.image,
          category: b.brazo_category || 'Clásico',
          availableSlice: b.availableSlice !== false,
          availableFull: b.availableFull !== false,
          available: b.available !== false,
          bajoPedido: b.bajoPedido === true,
          sortOrder: b.sortOrder || 999
        })));
      }
      
      // Galletas
      const galletasResponse = await fetch(`${BACKEND_URL}/api/products?category=galletas&${CB}`);
      if (galletasResponse.ok) {
        const galletasData = await galletasResponse.json();
        const mapped = galletasData.map(g => ({
          id: g.id,
          name: g.name,
          slug: g.slug,
          description: g.description,
          priceUnit: g.priceUnit || 1.50,
          pricePack5: g.pricePack5 || 6.00,
          pricePack12: g.pricePack12 || 14.00,
          price: g.price,
          image: g.image,
          available: g.available !== false
        }));
        // Choco Chips siempre primero
        mapped.sort((a, b) => {
          const aChoco = a.name.toLowerCase().includes('choco chip');
          const bChoco = b.name.toLowerCase().includes('choco chip');
          if (aChoco && !bChoco) return -1;
          if (!aChoco && bChoco) return 1;
          return 0;
        });
        setGalletas(mapped);
      }

      // Galletas Especiales
      const galletasEspecialesResponse = await fetch(`${BACKEND_URL}/api/products?category=galletas_especiales&${CB}`);
      if (galletasEspecialesResponse.ok) {
        const galletasEspecialesData = await galletasEspecialesResponse.json();
        setGalletasEspeciales(galletasEspecialesData.map(g => ({
          id: g.id,
          name: g.name,
          description: g.description,
          price: g.price || 0,
          image: g.image,
          available: g.available !== false
        })));
      }

      // Packs
      const packsResponse = await fetch(`${BACKEND_URL}/api/products?category=packs&${CB}`);
      if (packsResponse.ok) {
        const packsData = await packsResponse.json();
        setPacks(packsData.map(p => ({
          id: p.id,
          name: p.name,
          description: p.description,
          price: p.price || p.basePrice || 0,
          quantity: p.quantity,
          basePrice: p.basePrice,
          minQuantity: p.minQuantity,
          maxQuantity: p.maxQuantity,
          packType: p.packType,
          image: p.image,
          available: p.available !== false
        })));
      }
      
      // Pasteles
      const pastelesResponse = await fetch(`${BACKEND_URL}/api/products?category=pasteles&${CB}`);
      if (pastelesResponse.ok) {
        const pastelesData = await pastelesResponse.json();
        setPasteles(pastelesData.map(p => {
          let sizes, flavors, comboPrice, comboDescription;
          
          if (p.isConfigurable && p.slug === 'la-reina') {
            sizes = [
              { id: 'mediana', name: 'Mediana', portions: '12-15 personas', price: 28 },
              { id: 'grande', name: 'Grande', portions: '20-26 personas', price: 35 }
            ];
          } else if (p.isTresLeches && p.slug === 'tres-leches') {
            sizes = [
              { id: 'mediana', name: 'Pequeña (Redonda)', portions: '~6 personas', price: 13 },
              { id: 'grande', name: 'Grande (Rectangular)', portions: '~12 personas', price: 22 }
            ];
            flavors = [
              { id: 'clasica', name: 'Clásica', extraPrice: 0, comboGroup: 'standard' },
              { id: 'chocolate', name: 'Chocolate', extraPrice: 0, comboGroup: 'standard' },
              { id: 'maracuya', name: 'Maracuyá', extraPrice: 0, comboGroup: 'premium' },
              { id: 'ron_pope', name: 'Ron Pope (+$2)', extraPrice: 2, comboGroup: 'premium' }
            ];
            comboPrice = 28;
            comboDescription = 'Pequeña + Grande';
          }
          
          return {
            id: p.id,
            name: p.name,
            slug: p.slug,
            description: p.description,
            price: p.price || 0,
            portions: p.portions,
            image: p.image,
            isConfigurable: p.isConfigurable || false,
            isTresLeches: p.isTresLeches || false,
            bajoPedido: p.bajoPedido || false,
            sizes,
            flavors,
            comboPrice,
            comboDescription,
            available: p.available !== false
          };
        }));
      }
      
      // Panaderia
      const panaderiaResponse = await fetch(`${BACKEND_URL}/api/products?category=panaderia&${CB}`);
      if (panaderiaResponse.ok) {
        const panaderiaData = await panaderiaResponse.json();
        setPanaderia(panaderiaData.map(p => ({
          id: p.id,
          name: p.name,
          slug: p.slug,
          description: p.description,
          price: p.price || 0,
          image: p.image,
          hasOption: p.hasOption || false,
          options: p.options || [],
          bajoPedido: p.bajoPedido || false,
          available: p.available !== false
        })));
      }

      // Desayunos
      const desayunosResponse = await fetch(`${BACKEND_URL}/api/products?category=desayunos&${CB}`);
      if (desayunosResponse.ok) {
        const desayunosData = await desayunosResponse.json();
        setDesayunos(desayunosData.map(d => ({
          id: d.id,
          name: d.name,
          description: d.description,
          price: d.price || 0,
          image: d.image,
          hasOption: d.hasOption || false,
          options: d.options || [],
          available: d.available !== false
        })));
      }
      
      // Menu Salado
      const menuSaladoResponse = await fetch(`${BACKEND_URL}/api/products?category=menu_sal&${CB}`);
      if (menuSaladoResponse.ok) {
        const menuSaladoData = await menuSaladoResponse.json();
        setMenuSalado(menuSaladoData.map(m => ({
          id: m.id,
          name: m.name,
          description: m.description,
          price: m.price || 0,
          image: m.image,
          hasOption: m.hasOption || false,
          options: m.options || [],
          available: m.available !== false
        })));
      }
      
      // Bebidas Frias
      const bebidasFriasResponse = await fetch(`${BACKEND_URL}/api/products?category=bebidas&subcategory=frias&${CB}`);
      if (bebidasFriasResponse.ok) {
        const bebidasFriasData = await bebidasFriasResponse.json();
        setBebidasFrias(bebidasFriasData.map(b => ({
          id: b.id,
          name: b.name,
          description: b.description,
          price: b.price || 0,
          image: b.image,
          hasOption: b.hasOption || false,
          options: b.options || [],
          available: b.available !== false
        })));
      }
      
      // Bebidas Calientes
      const bebidasCalientesResponse = await fetch(`${BACKEND_URL}/api/products?category=bebidas&subcategory=calientes&${CB}`);
      if (bebidasCalientesResponse.ok) {
        const bebidasCalientesData = await bebidasCalientesResponse.json();
        setBebidasCalientes(bebidasCalientesData.map(b => ({
          id: b.id,
          name: b.name,
          description: b.description,
          price: b.price || 0,
          image: b.image,
          hasOption: b.hasOption || false,
          options: b.options || [],
          available: b.available !== false
        })));
      }
      
    } catch (error) {
      console.error('Error loading products from backend:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  return {
    brazosGitanos,
    galletas,
    galletasEspeciales,
    packs,
    pasteles,
    panaderia,
    desayunos,
    menuSalado,
    bebidasFrias,
    bebidasCalientes,
    loadingProducts
  };
};

export default useProducts;
